// src/components/Onboarding/OnboardingForm.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

import {
  COURSES,
  ROLES,
  SKILLS,
  SCHOOLS_UNIVERSITIES,
  USAGE_INTENTS,
  UsageIntent,
} from "@/data/onboardingOptions";
import { LANGUAGES } from "@/data/languages";
import { useLanguage, useTranslation } from "@/context/LanguageContext";

import { SearchableSelect } from "./SearchableSelect";
import { SearchableMultiSelect } from "./SearchableMultiSelect";

const onboardingSchema = z.object({
  intent: z.enum(["study", "teacher", "work", "personal"]),
  school: z.string().trim().optional(),
  courses: z.array(z.string()).optional(),
  profile_role: z.string().trim().optional(),
  skills: z.array(z.string()).optional(),
});

type OnboardingFormValues = z.infer<typeof onboardingSchema>;

export function OnboardingForm() {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const [intent, setIntent] = useState<UsageIntent>("study");
  const [school, setSchool] = useState<string | null>(null);
  const [courses, setCourses] = useState<string[]>([]);
  const [profileRole, setProfileRole] = useState<string | null>(null);
  const [skills, setSkills] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const navigate = useNavigate();

  // Load current profile, pre-fill if needed
  useEffect(() => {
    const loadProfile = async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", auth.user.id)
        .maybeSingle();

      if (error) {
        console.error(error);
        return;
      }

      if (profile?.onboarding_completed) {
        navigate("/dashboard");
        return;
      }

      if (profile?.intent) setIntent(profile.intent as UsageIntent);
      if (profile?.school) setSchool(profile.school);
      if (profile?.courses) setCourses(profile.courses);
      if (profile?.profile_role) setProfileRole(profile.profile_role);
      if (profile?.skills) setSkills(profile.skills);
    };

    loadProfile();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Build payload according to intent
    const base: OnboardingFormValues = {
      intent,
      school: school || undefined,
      courses,
      profile_role: profileRole || undefined,
      skills,
    };

    // Custom required logic based on intent
    const newErrors: Record<string, string> = {};
    if (intent === "study" || intent === "teacher") {
      if (!school) newErrors.school = t("onboarding.selectSchool");
      if (!courses || courses.length === 0) newErrors.courses = t("onboarding.selectCourse");
    } else {
      if (!profileRole) newErrors.profile_role = t("onboarding.selectRole");
      if (!skills || skills.length === 0) newErrors.skills = t("onboarding.selectSkill");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const result = onboardingSchema.safeParse(base);
    if (!result.success) {
      toast.error("Please check your details and try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) {
        toast.error("You must be signed in to complete onboarding.");
        return;
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          intent,
          school,
          courses,
          profile_role: profileRole,
          skills,
          onboarding_completed: true,
        })
        .eq("id", auth.user.id);

      if (error) {
        console.error(error);
        toast.error("Something went wrong. Please try again.");
        return;
      }

      toast.success("Profile completed! Redirecting to your dashboard...");
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      toast.error("Unexpected error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStudyOrTeacher = intent === "study" || intent === "teacher";

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-md">
      <div className="space-y-2 w-full">
        <Label className="text-sm font-medium">
          {t("onboarding.chooseLanguage")}
        </Label>
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="h-12 rounded-xl border-2">
            <SelectValue placeholder={t("onboarding.chooseLanguage")}>
              {LANGUAGES.find((l) => l.code === language) && (
                <span className="flex items-center gap-2">
                  <span>{LANGUAGES.find((l) => l.code === language)?.flag}</span>
                  <span>{LANGUAGES.find((l) => l.code === language)?.nativeLabel}</span>
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.filter((lang) => ["english", "arabic", "french", "spanish", "turkish"].includes(lang.code)).map((lang) => (
              <SelectItem key={lang.code} value={lang.code}>
                <span className="flex items-center gap-2">
                  <span>{lang.flag}</span>
                  <span>{lang.nativeLabel}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2 w-full">
        <Label className="text-sm font-medium">
          {t("onboarding.howPlanToUse")}
        </Label>
        <Select value={intent} onValueChange={(val: UsageIntent) => setIntent(val)}>
          <SelectTrigger className="h-12 rounded-xl border-2">
            <SelectValue placeholder={t("onboarding.howPlanToUse")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="study">{t("onboarding.intentStudy")}</SelectItem>
            <SelectItem value="teacher">{t("onboarding.intentTeacher")}</SelectItem>
            <SelectItem value="work">{t("onboarding.intentWork")}</SelectItem>
            <SelectItem value="personal">{t("onboarding.intentPersonal")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isStudyOrTeacher ? (
        <>
          <SearchableSelect
            label={t("onboarding.schoolUniversity")}
            placeholder={t("onboarding.searchOrTypeSchool")}
            value={school}
            onChange={setSchool}
            options={SCHOOLS_UNIVERSITIES}
            required
            error={errors.school}
          />

          <SearchableMultiSelect
            label={t("onboarding.courses")}
            placeholder={t("onboarding.selectOrTypeCourses")}
            values={courses}
            onChange={setCourses}
            options={COURSES}
            required
            error={errors.courses}
          />
        </>
      ) : (
        <>
          <SearchableSelect
            label={t("onboarding.role")}
            placeholder={t("onboarding.searchOrTypeRole")}
            value={profileRole}
            onChange={setProfileRole}
            options={ROLES}
            required
            error={errors.profile_role}
          />

          <SearchableMultiSelect
            label={t("onboarding.skills")}
            placeholder={t("onboarding.selectOrTypeSkills")}
            values={skills}
            onChange={setSkills}
            options={SKILLS}
            required
            error={errors.skills}
          />
        </>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          className="rounded-xl h-10 px-4"
          onClick={() => navigate("/dashboard")}
        >
          {t("common.cancel")}
        </Button>

        <Button
          type="submit"
          className="rounded-xl h-10 px-6"
          disabled={isSubmitting}
        >
          {isSubmitting ? t("onboarding.saving") : t("common.save")}
        </Button>
      </div>
    </form>
  );
}
